import { useBlocker } from '@tanstack/react-router';
import { useCallback, useRef } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~shared/shadcn/ui/alert-dialog';

/**
 * Blocks in-app navigation while `shouldBlock` is true (e.g. a react-hook-form
 * `isDirty` flag) and asks the user to confirm discarding unsaved changes.
 * Render the returned `dialog` inside the guarded component.
 *
 * Call `allowNextNavigation()` before a programmatic navigate after a
 * successful save — `isDirty` will not clear until the next render, so the
 * blocker would otherwise intercept that navigation.
 */
export const useUnsavedChangesBlocker = (shouldBlock: boolean) => {
  const shouldBlockRef = useRef(shouldBlock);
  shouldBlockRef.current = shouldBlock;

  const allowNextNavigation = useCallback(() => {
    shouldBlockRef.current = false;
  }, []);

  const { proceed, reset, status } = useBlocker({
    shouldBlockFn: () => shouldBlockRef.current,
    withResolver: true,
  });
  const isBlocked = status === 'blocked';

  const dialog = (
    <AlertDialog
      open={isBlocked}
      onOpenChange={(open) => {
        if (!open) {
          reset?.();
        }
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
          <AlertDialogDescription>
            You have unsaved changes. If you leave now, your edits will be lost.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep editing</AlertDialogCancel>
          <AlertDialogAction onClick={proceed}>Discard changes</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { dialog, isBlocked, allowNextNavigation };
};
